import type { Location } from "react-router";
import { NavigationQueryProvider } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTenant } from "hooks/settings";
import { NotFound } from "components/notFound";
import { TitledTabs, TabSpec } from "components/common/titledTabs";
import { ScrapEyeDataDashboardDeviceList } from "components/scrapEyeDataDashboard/ScrapEyeDataDashboardDeviceList";
import { useTenantTranslation } from "hooks/formatters";

import { InstallationManagementTab } from "./installation/InstallationManagementTab";
import { ScrapEyeDataDashboardAnalysis } from "./analysis/ScrapEyeDataDashboardAnalysis";
import { TrainableAndCoverageTab } from "./trainableAndCoverage/TrainableAndCoverageTab";
import { ModelPerformanceTab } from "./modelPerformance/ModelPerformanceTab";
import { RedactFootageTab } from "./redaction/RedactFootageTab";

type TabKey =
  | "devices"
  | "analysis"
  | "installation"
  | "trainable"
  | "modelPerformance"
  | "redaction";

function ScrapEyeDataDashboardPageContent() {
  const { t } = useTenantTranslation();
  const { tabKey } = useParams();
  const location: Location<unknown> = useLocation();
  const navigate = useNavigate();
  const tenantName = useTenant();
  const selectedTabKey = tabKey ?? "devices";
  const setSelectedTabKey = (key: TabKey) => {
    void navigate(
      {
        pathname: `/${tenantName}/admin/scrap-eye/data-dashboard/${key}`,
        search: location.search,
        hash: location.hash,
      },
      { state: location.state }
    );
  };

  const tabSpecs: TabSpec<TabKey>[] = [
    {
      key: "devices",
      title: t("scrapEyeDevicesTitle"),
      content: <ScrapEyeDataDashboardDeviceList />,
    },
    {
      key: "analysis",
      title: t("heatCoverageAnalysis"),
      content: <ScrapEyeDataDashboardAnalysis />,
    },
    {
      key: "installation",
      title: t("installationManagementTab"),
      content: <InstallationManagementTab />,
    },
    {
      key: "trainable",
      title: t("coverageAndTrainableTab"),
      content: <TrainableAndCoverageTab />,
    },
    {
      key: "modelPerformance",
      title: t("modelPerformanceTab"),
      content: <ModelPerformanceTab />,
    },
    {
      key: "redaction",
      title: t("redactFootageTab"),
      content: <RedactFootageTab />,
    },
  ];

  const selectedTab = tabSpecs.find((tab) => tab.key === selectedTabKey);
  if (!selectedTab) return <NotFound />;

  return (
    <TitledTabs
      tabSpecs={tabSpecs}
      selectedTabKey={selectedTab.key}
      onChange={setSelectedTabKey}
    />
  );
}

export default function ScrapEyeDataDashboardPage() {
  return (
    <NavigationQueryProvider>
      <ScrapEyeDataDashboardPageContent />
    </NavigationQueryProvider>
  );
}
